import React, { useRef, useEffect, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const STEPS = [
  {
    num: '01',
    tag: 'START',
    title: 'Upload or Start Fresh',
    body: 'Click "Build your Resume". You can either upload your existing resume (PDF/DOCX) to let our AI auto-fill your details, or start with a completely blank slate.',
    highlight: 'Tip: Uploading saves you time by filling in 80% of the form instantly.',
  },
  {
    num: '02',
    tag: 'FILL',
    title: 'Complete the Wizard',
    body: 'Follow our simple, guided steps to add your Contact Info, LinkedIn, GitHub, Experience, Education, and Skills. If you uploaded a resume, just verify the extracted data.',
    highlight: 'Everything you type is auto-saved locally. You won\'t lose your progress.',
  },
  {
    num: '03',
    tag: 'TARGET',
    title: 'Add a Job Description (Optional)',
    body: 'Applying for a specific role? Paste the Job Description (JD) in the "Specific JD?" step. Our AI will scan it and highlight the crucial ATS keywords you need to include.',
    highlight: 'AI extracts ~15 target keywords to help you beat the ATS.',
  },
  {
    num: '04',
    tag: 'GENERATE',
    title: 'Review, Edit & Download',
    body: 'Hit "Generate" and watch the magic happen. You can use the built-in AI chat to rewrite bullet points, change the visual template, and finally download your resume.',
    highlight: 'Export an industry-standard PDF instantly. No sign-ups required.',
  },
];

export default function StepsSection() {
  const sectionRef = useRef(null);
  const progressLineRef = useRef(null);
  const dotRef = useRef(null);
  const timelineTrackRef = useRef(null);
  const stepCardRefs = useRef([]);
  const stepMarkerRefs = useRef([]);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // GSAP animations (desktop only)
  useEffect(() => {
    if (isMobile) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top center',
          end: 'bottom center',
          scrub: 1.5,
        },
      });

      if (progressLineRef.current) {
        tl.to(progressLineRef.current, { height: '100%', ease: 'none' }, 0);
      }
      if (dotRef.current) {
        tl.to(dotRef.current, { top: '100%', ease: 'none' }, 0);
      }

      for (let i = 0; i < STEPS.length; i++) {
        const marker = stepMarkerRefs.current[i];
        const card = stepCardRefs.current[i];
        if (!marker || !card) continue;
        ScrollTrigger.create({
          trigger: card,
          start: 'top 70%',
          onEnter: () => {
            gsap.to(marker, {
              backgroundColor: '#FCA311',
              borderColor: '#E59200',
              scale: 1.5,
              boxShadow: '0 0 15px rgba(252,163,17,0.8)',
              duration: 0.4,
              ease: 'back.out(2)',
            });
          },
          onLeaveBack: () => {
            gsap.to(marker, {
              backgroundColor: 'var(--lp-bg)',
              borderColor: 'var(--lp-border)',
              scale: 1,
              boxShadow: 'none',
              duration: 0.4,
            });
          }
        });
      }

      stepCardRefs.current.forEach((card, idx) => {
        if (!card) return;
        gsap.set(card, { transformPerspective: 1000 });
        gsap.fromTo(card, 
          {
            opacity: 0,
            x: 40,
            y: 30,
            rotationY: -8,
            scale: 0.95
          },
          {
            opacity: 1,
            x: 0,
            y: 0,
            rotationY: 0,
            scale: 1,
            duration: 0.9,
            ease: 'back.out(1.2)',
            scrollTrigger: {
              trigger: card,
              start: 'top 85%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      });
    }, sectionRef);

    return () => ctx.revert();
  }, [isMobile]);

  // Sync timeline height and marker positions to cards
  useEffect(() => {
    if (isMobile) return;
    const updateLayout = () => {
      if (!timelineTrackRef.current || stepCardRefs.current.length === 0) return;
      const cards = stepCardRefs.current;
      if (!cards[0] || !cards[STEPS.length - 1]) return;

      const firstPos = cards[0].offsetTop;
      const lastPos = cards[STEPS.length - 1].offsetTop + cards[STEPS.length - 1].offsetHeight;

      timelineTrackRef.current.style.top = `${firstPos}px`;
      timelineTrackRef.current.style.height = `${lastPos - firstPos}px`;

      for (let i = 0; i < STEPS.length; i++) {
        const marker = stepMarkerRefs.current[i];
        const card = cards[i];
        if (!marker || !card) continue;
        if (i === 0) {
          marker.style.top = `${card.offsetTop}px`;
        } else if (i === STEPS.length - 1) {
          marker.style.top = `${card.offsetTop + card.offsetHeight}px`;
        } else {
          marker.style.top = `${card.offsetTop + card.offsetHeight / 2}px`;
        }
      }

      ScrollTrigger.refresh();
    };

    updateLayout();
    
    // Set up ResizeObserver to recalculate on element size changes (e.g. font loaded, layout shift)
    const resizeObserver = new ResizeObserver(() => {
      updateLayout();
    });

    stepCardRefs.current.forEach((card) => {
      if (card) resizeObserver.observe(card);
    });

    window.addEventListener('resize', updateLayout);
    window.addEventListener('load', updateLayout);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateLayout);
      window.removeEventListener('load', updateLayout);
    };
  }, [isMobile]);

  // Mobile: simple IntersectionObserver
  useEffect(() => {
    if (!isMobile) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    stepCardRefs.current.forEach((card) => {
      if (card) {
        card.classList.add('reveal');
        observer.observe(card);
      }
    });
    return () => observer.disconnect();
  }, [isMobile]);

  return (
    <section
      id="how-it-works"
      ref={sectionRef}
      className="py-10 sm:py-32 transition-colors duration-300"
      style={{ background: 'var(--lp-bg)' }}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="text-center mb-16 md:mb-24">
          <p className="text-xs font-bold text-brand-gz-purple tracking-[0.2em] uppercase mb-4">
            HOW IT WORKS
          </p>
          <h2
            className="font-display-gz font-black leading-tight tracking-tight mx-auto max-w-4xl"
            style={{ fontSize: 'clamp(28px, 3.5vw, 40px)', color: 'var(--lp-text)' }}
          >
            How to build your resume in <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FCA311] to-[#E59200]">minutes</span>
          </h2>
          <p className="text-lg mt-4 font-medium" style={{ color: 'var(--lp-text-muted)' }}>
            A step-by-step guide to landing your dream job
          </p>
        </div>

        {/* Main Layout */}
        <div className="relative max-w-5xl mx-auto md:pl-24">
          {/* ── Left Column: Timeline (desktop only) ── */}
          <div 
            ref={timelineTrackRef}
            className="hidden md:block absolute left-10 w-0.5 -translate-x-1/2 z-0" 
            style={{ background: 'var(--lp-border)' }}
          >
            {/* Progress line */}
            <div
              ref={progressLineRef}
              className="w-1 absolute left-1/2 -translate-x-1/2 top-0 rounded-full"
              style={{
                height: '0%',
                background: 'linear-gradient(to bottom, #14213D, #FCA311)',
                boxShadow: '0 0 15px rgba(252,163,17,0.6)',
              }}
              aria-hidden="true"
            />

            {/* Traveling dot */}
            <div
              ref={dotRef}
              className="w-6 h-6 rounded-full absolute left-1/2 -translate-x-1/2 -translate-y-1/2 z-10"
              style={{
                top: '0%',
                background: 'linear-gradient(135deg, #FCA311, #14213D)',
                boxShadow: '0 0 20px rgba(252,163,17,0.8), inset 0 2px 4px rgba(255,255,255,0.4)',
                animation: 'pulse-ring-gz 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                border: '2px solid white'
              }}
              aria-hidden="true"
            />
          </div>

          {/* Step markers */}
          {STEPS.map((_, i) => (
            <div
              key={`marker-${i}`}
              ref={(el) => { stepMarkerRefs.current[i] = el; }}
              className="hidden md:block w-4 h-4 rounded-full border-[3px] absolute left-10 -translate-x-1/2 -translate-y-1/2 z-20 transition-all duration-300"
              style={{
                borderColor: 'var(--lp-border)',
                background: 'var(--lp-bg)',
              }}
              aria-hidden="true"
            />
          ))}

          {/* ── Right Column: Step Cards ── */}
          <div className="flex flex-col gap-6 md:gap-10">
            {STEPS.map((step, idx) => (
              <div
                key={step.num}
                ref={(el) => { stepCardRefs.current[idx] = el; }}
                className="rounded-3xl p-6 md:p-8 transition-colors duration-300 relative overflow-hidden group"
                style={{
                  background: 'var(--lp-bg-alt)',
                  border: '1px solid var(--lp-border)',
                  boxShadow: '0 10px 40px -10px rgba(0,0,0,0.05)'
                }}
              >
                {/* Subtle gradient glow behind the card */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-brand-gz-purple/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

                {/* Top row: step label + tag */}
                <div className="flex items-center gap-3 mb-4 relative z-10">
                  <span className="font-display-gz text-lg md:text-xl font-black tracking-widest uppercase bg-clip-text text-transparent bg-gradient-to-r from-brand-gz-purple to-purple-400">
                    STEP {step.num}
                  </span>
                  <span className="bg-brand-gz-purple/10 text-brand-gz-purple text-[10px] md:text-xs font-bold tracking-[0.2em] uppercase px-3 py-1 rounded-full border border-brand-gz-purple/20">
                    {step.tag}
                  </span>
                </div>

                <h3
                  className="text-xl md:text-2xl font-display-gz font-bold mb-2 relative z-10"
                  style={{ color: 'var(--lp-text)' }}
                >
                  {step.title}
                </h3>

                <p className="text-sm md:text-base leading-relaxed mb-4 relative z-10" style={{ color: 'var(--lp-text-muted)' }}>
                  {step.body}
                </p>

                <div className="bg-brand-gz-purple/5 border-l-4 border-brand-gz-purple px-4 py-3 rounded-r-xl relative z-10">
                  <p className="text-sm text-brand-gz-purple font-semibold">
                    {step.highlight}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
